import {
  EMBEDDED,
  EmbeddedMessageMedium,
  MessageMedium,
  WHATS_APP,
  WhatsAppMedium,
} from 'constants/messageMediums';
import assign from 'lodash/assign';
import { SearchCombo, SearchField } from 'modules/domains/segmentation';
import {
  AttributionMode,
  Campaign,
  CustomConversion,
  RawConversion,
  SendNodeData,
} from 'modules/workflows';
import { getClientDataTypeFromEventType } from 'utils/dataTypes';
import { isEmpty } from '@iterable/utils';
import {
  ATTRIBUTION_MODE_LOOKUP,
  CampaignStateDefinition,
  DEFAULT_ATTRIBUTION_MODE_PERIOD_HOURS,
} from '../BaseSendEditor.constants';
import { CurrentProject, SendNodeResources } from '../BaseSendEditor.types';
import { isSTOEnabled } from './helpers';
import { addProjectIdToLabels } from './labels';

export const isNewCampaign = (campaign: Partial<Campaign>) => !campaign?.id;

export const isStartAndEndTimeTheSame = (startTime: string, endTime: string) =>
  startTime === endTime;

export const consolidateCampaignData = (nodeData: SendNodeData, resources: SendNodeResources) => {
  return assign({}, resources?.campaign || {}, nodeData?.resource?.campaign || {});
};

const getCustomConversionFromConversion = (conversion: RawConversion): CustomConversion => {
  const { events = [] } = conversion;
  const { name, attributionMode: mode } = events[0] || {};
  const attributionMode = ATTRIBUTION_MODE_LOOKUP[mode?.name] as AttributionMode;

  return {
    eventName: name,
    attributionMode: attributionMode ?? AttributionMode.ExplicitlySet,
    attributionPeriodHours: mode?.periodHours ?? DEFAULT_ATTRIBUTION_MODE_PERIOD_HOURS,
  };
};

export const getMultiConversionData = (campaign: Partial<Campaign> = {}) => {
  const { conversions = [] } = campaign;
  const customConversions = conversions.map(getCustomConversionFromConversion);
  return { customConversions };
};

/**
 * Convert what is returned from gql for `dataType` to a compatible format for upsertWorkflowCampaign
 */
export const getCorrectedDataType = (q: SearchField) => ({
  ...q,
  dataType: getClientDataTypeFromEventType(q.dataType) || q.dataType,
});

export const getDisplayUntilData = (campaign: Partial<Campaign> = {}) => {
  const { displayUntil = {} } = campaign;
  const queries = displayUntil?.searchQuery?.searchQueries || [];

  return {
    displayUntil: {
      ...displayUntil,
      ...(!isEmpty(displayUntil.searchQuery) && {
        searchQuery: {
          ...displayUntil?.searchQuery,
          searchQueries: queries.map((q: SearchField) => getCorrectedDataType(q)),
        } as SearchCombo,
      }),
    },
  };
};

export const parseCampaignForSaving = (
  campaign: Partial<Campaign> = {},
  project: CurrentProject,
  isEmbeddedMessagingJourneysEnabled = false,
) => {
  const { id } = project || {};
  const {
    campaignAlertsEnabled = false,
    customQuietHours,
    customRateLimit,
    ignoreFrequencyCap,
    dataScienceOptimizations,
    labels,
    mobileAppIds,
    name,
    priorityLevel,
    schedulerMsgParams,
    placementId,
    displayUntil,
  } = campaign;

  return {
    campaignAlertsEnabled,
    customQuietHours,
    ignoreFrequencyCap,
    labels: addProjectIdToLabels(labels, id),
    name,
    priorityLevel,
    ...getMultiConversionData(campaign),
    ...(isSTOEnabled(dataScienceOptimizations) && {
      dataScienceOptimizations,
      schedulerMsgParams,
    }),
    customRateLimit,
    mobileAppIds,
    ...(!!placementId && isEmbeddedMessagingJourneysEnabled && { placementId }),
    ...(!isEmpty(displayUntil) &&
      isEmbeddedMessagingJourneysEnabled &&
      getDisplayUntilData(campaign)),
  };
};

export const getBaseSendEditorSectionConfig = ({
  campaign = {},
  campaignId,
  messageMedium,
  hasPlacements,
  isWhatsAppGAEnabled,
}: {
  campaign: Partial<Campaign>;
  campaignId?: number;
  messageMedium: MessageMedium | EmbeddedMessageMedium | WhatsAppMedium;
  hasPlacements?: boolean;
  isWhatsAppGAEnabled?: boolean;
}) => {
  const isArchived = campaign?.campaignState === CampaignStateDefinition.Archived;

  if (isArchived) return {};

  const isEmbedded = messageMedium === EMBEDDED;
  const isWhatsApp = messageMedium === WHATS_APP;

  return {
    showNameInput: isEmbedded ? !!hasPlacements : true,
    showBackButton: isEmbedded && !!campaign.placementId && !campaignId,
    showPlacementSection: isEmbedded && !campaign.placementId && !!hasPlacements,
    showContentSection: isEmbedded ? !!campaign.placementId : true,
    showDisplaySection: isEmbedded && !!campaign.placementId,
    showDeliverySection: !isEmbedded && !isWhatsApp,
    showConversionSection: isWhatsApp ? isWhatsAppGAEnabled : !isEmbedded,
    showExperimentSection: !isEmbedded && !isWhatsApp,
    showAddPlacementsSection: isEmbedded && !campaign.placementId && !hasPlacements,
  };
};
